
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, ArrowLeft, Search, X } from "lucide-react";
import { useDialog } from "@/hooks/use-dialog";
import { PEOProviderForm } from "../PEOProviderForm";
import { useNavigate } from "react-router-dom";
import { AIControlPanel } from '../AIControlPanel';

interface PEOManagementHeaderProps {
  refetch: () => void;
  activeFilters?: any;
  setActiveFilters?: (filters: any) => void;
  providers?: any[];
  applyFilters?: (params: any) => void;
  clearFilters?: () => void;
  showDuplicates?: boolean;
  isSimplifyingNames?: boolean;
  onToggleDuplicates?: () => void;
  onSimplifyNames?: () => void;
  onRefresh?: () => void;
  onAddProvider?: () => void;
  isError?: boolean;
  showDebugInfo?: boolean;
  onToggleDebugInfo?: () => void;
  searchTerm?: string;
  onSearchChange?: (term: string) => void;
}

export const PEOManagementHeader = ({ 
  refetch,
  activeFilters,
  setActiveFilters,
  providers = [],
  applyFilters = () => {},
  clearFilters = () => {},
  showDuplicates = false,
  isSimplifyingNames = false,
  onToggleDuplicates = () => {},
  onSimplifyNames = () => {},
  onRefresh = () => {},
  onAddProvider,
  isError = false,
  showDebugInfo = false,
  onToggleDebugInfo = () => {},
  searchTerm = '',
  onSearchChange = () => {}
}: PEOManagementHeaderProps) => {
  const { open, close } = useDialog();
  const navigate = useNavigate();

  const handleOpenProviderForm = () => {
    if (onAddProvider) {
      onAddProvider();
    } else {
      open(
        <PEOProviderForm 
          onClose={close} 
          onSuccess={refetch} 
        />
      );
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleSearchClear = () => {
    onSearchChange('');
  };

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card className="md:col-span-2">
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleGoBack} 
              className="mr-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="ml-1">Back</span>
            </Button>
            <CardTitle>Manage PEO Providers</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search Bar */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search providers by name, certifications, states..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-10 pr-10"
            />
            {searchTerm && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSearchClear}
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              Add, edit, and manage PEO providers in the directory.
              {searchTerm && (
                <div className="text-sm text-muted-foreground mt-1">
                  Searching for: "{searchTerm}"
                </div>
              )}
            </div>
            <Button onClick={handleOpenProviderForm}>
              <Plus className="mr-2 h-4 w-4" />
              Add Provider
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
